import bundledJQuery from 'jquery'

import extra_storage from './extra_storage'

// Vendored from the retired `fluxxed_up` package (TK-1926). A thin jQuery.ajax wrapper that
// builds JSON requests against the Everplans API and returns jQuery Deferreds.

// Like the upstream helper, prefer a jQuery the host page already placed on `window.$` (embedded apps such as
// externalApp.js keep the host's instance) so requests inherit its AJAX defaults and prefilters. Fall back to the
// bundled copy when the global is absent or isn't jQuery.
const isJQuery = candidate => typeof candidate === 'function' && typeof candidate.ajax === 'function' && typeof candidate.Deferred === 'function'

export const resolveJQuery = () => {
  const globalJQuery = typeof window === 'undefined' ? null : window.$
  return isJQuery(globalJQuery) ? globalJQuery : bundledJQuery
}

const DEFAULT_ERROR_MESSAGE = "We're sorry, an unexpected error has occurred. Please try again; our engineers have been notified."

const defaultAdaptor = {
  serverBase() { return '' }, // default will use the relative path
  pathRoot() { return '/' },
  defaultHeaders() { return {} } // any headers that should be in every ajax request
}

export class EpAjaxAdaptor {
  serverBase() { return API_HOST }
  pathRoot() { return '/api/v1' }
  defaultHeaders() {
    const headers = {}
    const authToken = extra_storage.getItem('authToken')
    if (authToken)
      headers.Authorization = `Token token=${authToken}`
    return headers
  }
}

const defaultConfig = {
  additionalHeaders: {},
  stringify: true
}

const cleanSlashes = path => `${path.indexOf('/') === 0 ? '' : '/'}${path}`

// TODO: Success data will only be a string if a test fixture returns it, and test side effects should not dictate the
// structure of non-test code such as this. Remove this function and its invocation once this problem is fixed.
const isNotWhitespaceString = data => !(typeof data === 'string' && data.trim().length === 0)

const ensureDataIsObject = data => {
  if (typeof data !== 'object' && isNotWhitespaceString(data))
    return JSON.parse(data)

  return data
}

export const errorJSONFromXhr = error => {
  if (error.responseJSON)
    return error.responseJSON

  if (error.responseText) {
    try {
      return JSON.parse(error.responseText)
    } catch {
      // in the event there is an error message, but it's not JSON. Could be higher up server messages, etc..
      return {errors: [error.responseText]}
    }
  }

  return {errors: [DEFAULT_ERROR_MESSAGE]}
}

const jsonStatham = {
  setAdaptor(adaptor) { this.adaptor = adaptor },
  getAdaptor() { return this.adaptor || defaultAdaptor },
  buildRequest(path, method, data, config = defaultConfig) {
    const adaptor = this.getAdaptor()
    const opts = {
      // Default to JSON content, but allow default or additional headers to override this:
      headers: Object.assign({'Content-Type': 'application/json'}, adaptor.defaultHeaders(), config.additionalHeaders),
      url: adaptor.serverBase() + cleanSlashes(adaptor.pathRoot()) + cleanSlashes(path)
    }
    if (method)
      opts.method = method

    // Stringify data if it exists, is JSON, and config doesn't say not to stringify; pass it along as-is otherwise.
    if (data)
      opts.data = (opts.headers['Content-Type'] === 'application/json' && config.stringify !== false) ? JSON.stringify(data) : data
    return opts
  },
  sendRequest(request) {
    const $ = resolveJQuery()
    const promise = $.Deferred() // eslint-disable-line new-cap -- jQuery's Deferred factory is capitalized
    $.ajax(request)
      .done((data, result, response) => { promise.resolve(ensureDataIsObject(data), response.status) })
      .fail(error => { promise.reject(errorJSONFromXhr(error), error.status) })

    return promise
  },
  get(url) { return this.sendRequest(this.buildRequest(url, 'GET', null)) },
  patch(url, data) { return this.sendRequest(this.buildRequest(url, 'PATCH', data)) },
  put(url, data) { return this.sendRequest(this.buildRequest(url, 'PUT', data)) },
  post(url, data) { return this.sendRequest(this.buildRequest(url, 'POST', data)) },
  delete(url, data) { return this.sendRequest(this.buildRequest(url, 'DELETE', data)) },
  postFile(url, data) {
    // Taken from: http://stackoverflow.com/questions/12431760/html5-formdata-file-upload-with-rubyonrails
    // and http://stackoverflow.com/questions/21234106/upload-file-using-reactjs-via-blueimp-fileupload-jquery-plugin
    const request = this.buildRequest(url, 'POST', data, {stringify: false})

    // This header must be deleted so the server doesn't get angry that it is receiving multipart/form-data instead.
    // Setting it to multipart/form-data does not work because it does not contain the boundary, which is required.
    Reflect.deleteProperty(request.headers, 'Content-Type')
    request.cache = false
    request.processData = false
    request.contentType = false
    return this.sendRequest(request)
  }
}

jsonStatham.setAdaptor(new EpAjaxAdaptor())

export default jsonStatham
